import React, { createContext, useState, useContext, useEffect } from 'react';
import { useUser } from './userContext';

// Kontext erstellen
const ConfigContext = createContext(null);

// Provider-Komponente
export function ConfigProvider({ children }) {
    const { user } = useUser();
    const [config, setConfig] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // Config beim ersten Laden abrufen
    useEffect(() => {
        const fetchConfig = async () => {
            if (!user || !user.api_key) {
                setIsLoading(false);
                return;
            }

            try {
                // Alle benötigten Konfigurationsparameter auf einmal abrufen
                // HINWEIS: es dürfen nur ausgewählte Parameter geladen werden, die mit mod_ anfangen.
                // Ansonsten würden Datenschutzrechtliche Parameter geladen werden, die nicht für die UI gedacht sind.
                
                // Lade mod_ Parameter
                const modResponse = await fetch(`${process.env.REACT_APP_API_URI}/api/config/filtered/mod_`, {
                    headers: {
                        'Authorization': user.api_key,
                    },
                });
                
                // Lade time_corridor_ Parameter für Zeitkorridor-Einstellungen
                const timeCorridorResponse = await fetch(`${process.env.REACT_APP_API_URI}/api/config/filtered/time_corridor_`, {
                    headers: {
                        'Authorization': user.api_key,
                    },
                });
                
                // Lade use_vrp_optimization Parameter
                const vrpOptimizationResponse = await fetch(`${process.env.REACT_APP_API_URI}/api/config/filtered/use_vrp_optimization`, {
                    headers: {
                        'Authorization': user.api_key,
                    },
                });
                
                // Lade session_ Parameter für Session-Konfiguration
                const sessionResponse = await fetch(`${process.env.REACT_APP_API_URI}/api/config/filtered/session_`, {
                    headers: {
                        'Authorization': user.api_key,
                    },
                });

                // Lade codi_enabled (globales Feature-Flag) – explizit nur diesen Key, keine codi_* Secrets laden
                const codiEnabledResponse = await fetch(`${process.env.REACT_APP_API_URI}/api/config/filtered/codi_enabled`, {
                    headers: {
                        'Authorization': user.api_key,
                    },
                });

                // Lade UI-Parameter (z. B. ui.show_deactivated_vehicles.*)
                const uiResponse = await fetch(`${process.env.REACT_APP_API_URI}/api/config/filtered/ui.`, {
                    headers: {
                        'Authorization': user.api_key,
                    },
                });

                const ortoolsResponse = await fetch(`${process.env.REACT_APP_API_URI}/api/config/filtered/ortools_`, {
                    headers: {
                        'Authorization': user.api_key,
                    },
                });

                const transportTypeCatalogResponse = await fetch(`${process.env.REACT_APP_API_URI}/api/config/filtered/transport_type_catalog`, {
                    headers: {
                        'Authorization': user.api_key,
                    },
                });

                const waitingQueueConfigResponse = await fetch(`${process.env.REACT_APP_API_URI}/api/config/filtered/waiting_queue_config`, {
                    headers: {
                        'Authorization': user.api_key,
                    },
                });

                if (
                    modResponse.ok &&
                    timeCorridorResponse.ok &&
                    vrpOptimizationResponse.ok &&
                    sessionResponse.ok &&
                    codiEnabledResponse.ok &&
                    uiResponse.ok &&
                    ortoolsResponse.ok &&
                    transportTypeCatalogResponse.ok &&
                    waitingQueueConfigResponse.ok
                ) {
                    const modData = await modResponse.json();
                    const timeCorridorData = await timeCorridorResponse.json();
                    const vrpOptimizationData = await vrpOptimizationResponse.json();
                    const sessionData = await sessionResponse.json();
                    const codiEnabledData = await codiEnabledResponse.json();
                    const uiData = await uiResponse.json();
                    const ortoolsData = await ortoolsResponse.json();
                    const transportTypeCatalogData = await transportTypeCatalogResponse.json();
                    const waitingQueueConfigData = await waitingQueueConfigResponse.json();
                    
                    // Kombiniere alle Konfigurationen
                    const data = {
                        ...modData,
                        ...timeCorridorData,
                        ...vrpOptimizationData,
                        ...sessionData,
                        ...codiEnabledData,
                        ...uiData,
                        ...ortoolsData,
                        ...transportTypeCatalogData,
                        ...waitingQueueConfigData
                    };

                    // Konvertiere die erhaltenen Werte in ihre korrekten Typen
                    const parsedConfig = Object.entries(data).reduce((acc, [key, value]) => {
                        if (key === 'transport_type_catalog' || key === 'waiting_queue_config') {
                            if (typeof value === 'string') {
                                try {
                                    acc[key] = JSON.parse(value);
                                } catch (parseError) {
                                    acc[key] = value;
                                }
                            } else {
                                acc[key] = value;
                            }
                            return acc;
                        }
                        // Boolean-Werte korrekt parsen
                        if (value === 'true' || value === 'false') {
                            acc[key] = value === 'true';
                        } else {
                            acc[key] = value;
                        }
                        return acc;
                    }, {});

                    //console.log('Konfiguration geladen:', parsedConfig);
                    setConfig(parsedConfig);
                } else {
                    throw new Error('Fehler beim Laden der Konfiguration');
                }
            } catch (err) {
                console.error('Config loading error:', err);
                setError(err);
            } finally {
                setIsLoading(false);
            }
        };

        fetchConfig();
    }, [user]);

    // Funktion zum Abrufen einzelner Konfigurationsparameter mit Fallback-Wert
    const getConfigValue = (key, defaultValue = null) => {
        return config[key] !== undefined ? config[key] : defaultValue;
    };

    return (
        <ConfigContext.Provider value={{ config, isLoading, error, getConfigValue }}>
            {children}
        </ConfigContext.Provider>
    );
}

// Custom Hook für einfachen Zugriff
export function useConfig() {
    const context = useContext(ConfigContext);
    if (!context) {
        throw new Error('useConfig must be used within a ConfigProvider');
    }
    return context;
}
